import { Box, Typography } from '@mui/material';
import { Button } from '@components/button';
import { useState, MouseEvent, useEffect, Fragment } from 'react';
import { InView } from 'react-intersection-observer';
import { shallow } from 'zustand/shallow';
import { CaretDown, SlidersH } from '@assets/svgs';
import { useSearchQuoteStatusChange } from '@services/quotes';
import { useSearchQuoteRatings } from '@services/quoteRating/useSearchQuoteRatings';
import { useSearchNotes } from '@services/note';
import Loading from '@components/Loading';
import { QuoteActivityItem } from '@pages/QuoteDetails/sidebarItems/summary/quoteActivity/QuoteActivityItem';
import { Menu, MenuItemType } from '@components/menu';
import { useStore } from '@store/GlobalStore';
import {
  IFiltering,
  IQuoteActivity,
  ISorting,
  Sorting,
  externalFilterInitialState,
  internalFilterInitialState,
} from '@pages/QuoteDetails/sidebarItems/summary/quoteActivity/QuoteActivity.constants';
import { SidebarHeader } from '@pages/QuoteDetails/sidebarItems/SidebarHeader';
import { SearchField } from '@components/search';
import { useSearchDocumentsInfinite } from '@services/document';
import { scrollToElement } from '@utils/scrollToElement';
import { DocumentType } from '@models/document';
import { useQuoteActivityData } from '@pages/QuoteDetails/sidebarItems/summary/quoteActivity/getQuoteActivityData';
import { useQuote } from '@services/quotes';
import { useSearchUnderwritingActivity } from '@services/underwritingReferral';

export const QuoteActivity = ({ quoteId, quoteType }: IQuoteActivity) => {
  const { isInternalUser } = useStore((state) => state.userAuth, shallow);
  const [sorting, setSorting] = useState<ISorting>({ type: Sorting.Newest, anchorEl: null });
  const [filter, setFilter] = useState<IFiltering>({
    list: isInternalUser ? internalFilterInitialState : externalFilterInitialState,
    anchorEl: null,
  });
  const [highlightedItem, setHighlightedItem] = useState<string | null>(null);
  const [textInput, setTextInput] = useState<string>('');
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(false);

  const { quote } = useQuote(quoteId);

  const {
    underwritingActivity: quoteUnderwritingActivity,
    isLoading: isQuoteUnderwritingActivityLoading,
    isFetching: isFetchingMoreQuoteUnderwritingActivity,
    fetchNextPage: fetchMoreQuoteUnderwritingActivity,
  } = useSearchUnderwritingActivity({
    quoteId,
    pageLimit: 10,
    sort: sorting.type === Sorting.Newest ? '-createdAt' : '+createdAt',
  });

  const {
    quoteStatusChange,
    isLoading: isQuoteStatusChangeLoading,
    isFetching: isFetchingMoreStatusChanges,
    fetchNextPage: fetchMoreStatusChanges,
  } = useSearchQuoteStatusChange({
    quoteId,
    pageLimit: 10,
    sort: sorting.type === Sorting.Newest ? '-createdAt' : '+createdAt',
  });

  const {
    data: searchQuoteRatings,
    isLoading: isQuoteRatingsLoading,
    isFetching: isFetchingMoreQuoteRatings,
    fetchNextPage: fetchMoreQuoteRatings,
  } = useSearchQuoteRatings({
    quoteId,
    enabled: isInternalUser,
    params: {
      pageLimit: 10,
      sort: sorting.type === Sorting.Newest ? '-createdAt' : '+createdAt',
      hasRateChangeAnalysis: false,
    },
  });

  const {
    searchDocuments,
    isLoading: isDocumentsLoading,
    isFetchingNextPage: isFetchingMoreDocuments,
    fetchNextPage: fetchMoreDocuments,
  } = useSearchDocumentsInfinite(
    {
      quoteId: quoteId as string,
      documentTypeId: [DocumentType.QuoteProposal.id, DocumentType.Application.id],
      documentGenerationStatus: 'Completed',
      pageLimit: 10,
      sort: sorting.type === Sorting.Newest ? '-createdAt' : '+createdAt',
    },
    isInternalUser,
  );

  const {
    searchNotes,
    isLoading: isSearchNotesLoading,
    isFetchingNextPage: isFetchingMoreNotes,
    fetchNextPage: fetchMoreNotes,
  } = useSearchNotes({
    quoteId: quoteId as string,
    pageLimit: 10,
    sort: sorting.type === Sorting.Newest ? '-createdAtOrReplacedAt,-createdAt' : '+createdAtOrReplacedAt,+createdAt',
    includeDeleted: true,
  });

  const isLoading =
    isQuoteStatusChangeLoading ||
    isQuoteRatingsLoading ||
    isSearchNotesLoading ||
    isDocumentsLoading ||
    isQuoteUnderwritingActivityLoading;

  const allStatusChanges = quoteStatusChange?.pages.flatMap((page) => page.items);
  const allQuoteRatings = searchQuoteRatings?.pages.flatMap((page) => page.items);
  const allNotes = searchNotes?.pages?.flatMap((page) => page.items);
  const allDocuments = searchDocuments?.pages?.flatMap((page: any) => page.items);
  const allQuoteUnderwritingActivity = quoteUnderwritingActivity?.pages?.flatMap((page) => page.items);

  const { items, dateHeaderItems, lastLoadedItems } = useQuoteActivityData(
    {
      allStatusChanges,
      allQuoteRatings,
      allNotes,
      allDocuments,
      allQuoteUnderwritingActivity,
      quote: quote
        ? {
            id: quote.id,
            createdAt: quote.createdAt instanceof Date ? quote.createdAt.toISOString() : quote.createdAt,
            createdByUser: quote.createdByUser
              ? {
                  firstName: quote.createdByUser.firstName,
                  lastName: quote.createdByUser.lastName,
                }
              : undefined,
          }
        : undefined,
    },
    { filter, sorting, textInput },
  );

  useEffect(() => {
    if (
      isScrolledToBottom &&
      !isFetchingMoreDocuments &&
      allDocuments &&
      searchDocuments?.pages[0]?.pagination?.total &&
      searchDocuments?.pages[0]?.pagination?.total > allDocuments.length
    ) {
      fetchMoreDocuments();
    }
  }, [allDocuments, fetchMoreDocuments, isFetchingMoreDocuments, isScrolledToBottom, searchDocuments]);

  useEffect(() => {
    if (
      isScrolledToBottom &&
      !isFetchingMoreNotes &&
      allNotes &&
      searchNotes?.pages[0]?.pagination?.total &&
      searchNotes?.pages[0]?.pagination?.total > allNotes.length
    ) {
      fetchMoreNotes();
    }
  }, [allNotes, fetchMoreNotes, isFetchingMoreNotes, isScrolledToBottom, searchNotes]);

  useEffect(() => {
    if (
      isScrolledToBottom &&
      !isFetchingMoreQuoteRatings &&
      allQuoteRatings &&
      searchQuoteRatings?.pages[0]?.pagination?.total &&
      searchQuoteRatings?.pages[0]?.pagination?.total > allQuoteRatings.length
    ) {
      fetchMoreQuoteRatings();
    }
  }, [allQuoteRatings, fetchMoreQuoteRatings, isFetchingMoreQuoteRatings, isScrolledToBottom, searchQuoteRatings]);

  useEffect(() => {
    if (
      isScrolledToBottom &&
      !isFetchingMoreStatusChanges &&
      allStatusChanges &&
      quoteStatusChange?.pages[0]?.pagination?.total &&
      quoteStatusChange?.pages[0]?.pagination?.total > allStatusChanges.length
    ) {
      fetchMoreStatusChanges();
    }
  }, [allStatusChanges, fetchMoreStatusChanges, isFetchingMoreStatusChanges, isScrolledToBottom, quoteStatusChange]);

  useEffect(() => {
    if (
      isScrolledToBottom &&
      !isFetchingMoreStatusChanges &&
      allQuoteUnderwritingActivity &&
      quoteUnderwritingActivity?.pages[0]?.pagination?.total &&
      quoteUnderwritingActivity?.pages[0]?.pagination?.total > allQuoteUnderwritingActivity.length
    ) {
      fetchMoreQuoteUnderwritingActivity();
    }
  }, [
    allQuoteUnderwritingActivity,
    fetchMoreQuoteUnderwritingActivity,
    isFetchingMoreQuoteUnderwritingActivity,
    isScrolledToBottom,
    quoteUnderwritingActivity,
    isFetchingMoreStatusChanges,
  ]);

  const handleOpenSorting = (event: MouseEvent<HTMLButtonElement>) =>
    setSorting((prev) => ({ ...prev, anchorEl: event.currentTarget }));
  const handleCloseSorting = () => setSorting((prev) => ({ ...prev, anchorEl: null }));
  const handleSortingItemClick = (_: number, innerText?: string) => {
    const currentMenuItem = innerText?.split(' ');
    setSorting((prev) => ({ ...prev, type: currentMenuItem?.[0] as Sorting }));
  };

  const handleOpenFilter = (event: MouseEvent<HTMLButtonElement>) =>
    setFilter((prev) => ({ ...prev, anchorEl: event.currentTarget }));
  const handleCloseFilter = () => setFilter((prev) => ({ ...prev, anchorEl: null }));
  const handleFilterItemClick = (_: number, innerText?: string) => {
    if (innerText) {
      setFilter((prev) => {
        const checkedMenuItems = prev.list.filter((el) => el.checked);
        const [current] = prev.list.filter((el) => el.text === innerText);
        if (checkedMenuItems.length === 1 && current.checked) return prev;
        return {
          ...prev,
          list: prev.list.map((el) => (el.text === innerText ? { ...el, checked: !el.checked } : el)),
        };
      });
    }
  };

  const handleSelectItem = async (nodeId: string) => {
    setHighlightedItem('');
    if (!nodeId) return;

    if (textInput !== '') {
      setTextInput('');
      await new Promise((resolve) => setTimeout(resolve, 200));
    }

    const element = document.getElementById(nodeId);
    if (element === null) {
      await fetchMoreNotes().then(() => {
        const newElement = document.getElementById(nodeId);
        scrollToElement(newElement).then(() => setHighlightedItem(nodeId));
      });
    } else {
      scrollToElement(element).then(() => setHighlightedItem(nodeId));
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <SearchField
        autofocus
        overrideValue={textInput}
        onChange={(e) => setTextInput(e)}
        onPressEndIcon={() => setTextInput('')}
        variant='standard'
        sx={{ margin: '16px auto', width: '90%' }}
      />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid rgba(0, 0, 0, 0.15)' }}>
        <Button variant='text' onClick={handleOpenFilter} startIcon={<SlidersH />}>
          Filters
        </Button>
        <Menu
          anchorEl={filter.anchorEl}
          open={Boolean(filter.anchorEl)}
          items={filter.list}
          onClose={handleCloseFilter}
          onClickMenuItem={handleFilterItemClick}
        />
        <Button variant='text' onClick={handleOpenSorting} endIcon={<CaretDown />}>
          {sorting.type}
        </Button>
        <Menu
          anchorEl={sorting.anchorEl}
          open={Boolean(sorting.anchorEl)}
          items={[
            { type: MenuItemType.Default, text: 'Newest First' },
            { type: MenuItemType.Default, text: 'Oldest First' },
          ]}
          onClose={handleCloseSorting}
          onClickMenuItem={handleSortingItemClick}
        />
      </Box>
      <Box sx={{ overflowY: 'auto', overflowX: 'hidden', margin: '0px', flexGrow: '1' }}>
        {isLoading ? (
          <Loading />
        ) : items.length === 0 ? (
          <Typography>No items to display</Typography>
        ) : (
          items.map((el, index) => {
            const isHeader = Object.values(dateHeaderItems).includes(el.id);
            const isLastItem = Object.values(lastLoadedItems)
              .flat()
              .some((item) => item?.id === el.id);
            if (isHeader) {
              const [dateHeader] = Object.entries(dateHeaderItems).filter((entry) => entry[1] === el.id);
              return (
                <Box key={el.id} width='100%'>
                  <SidebarHeader title={dateHeader[0]} />
                  {isLastItem && (
                    <InView id='scrollToBottomObserver' as='div' onChange={(inView) => setIsScrolledToBottom(inView)} />
                  )}
                  <QuoteActivityItem
                    item={el}
                    highlightedItem={highlightedItem}
                    isLast={index === items.length - 1}
                    quoteType={quoteType}
                    onSelectItem={handleSelectItem}
                    policyNumber={quote?.policyNumber?.policyNumber}
                  />
                </Box>
              );
            }
            return (
              <Fragment key={el.id}>
                {isLastItem && (
                  <InView id='scrollToBottomObserver' as='div' onChange={(inView) => setIsScrolledToBottom(inView)} />
                )}
                <QuoteActivityItem
                  item={el}
                  highlightedItem={highlightedItem}
                  isLast={index === items.length - 1}
                  quoteType={quoteType}
                  onSelectItem={handleSelectItem}
                  policyNumber={quote?.policyNumber?.policyNumber}
                />
              </Fragment>
            );
          })
        )}
        {!isLoading && (isFetchingMoreNotes || isFetchingMoreQuoteRatings || isFetchingMoreStatusChanges) && (
          <Box sx={{ margin: '20px' }}>
            <Loading />
          </Box>
        )}
      </Box>
    </Box>
  );
};
